import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { api, ApiError, ensureCsrfCookie, setUnauthenticatedHandler } from '@/lib/api'
import type { AuthUser, PermissionKey, DataScope } from '@/lib/permissions'
import {
  can,
  canAny,
  canAll,
  canSeeRestrictedModule,
  hasScope,
  hasModuleAccess,
  isSuperAdmin,
} from '@/lib/permissions'
import { moduleDeclaredPermissions } from '@/app/routes/moduleRegistry'
import { setObservabilityUser } from '@/lib/observability'
import { signOutOfChat } from '@/features/chat/chatSession'
import { PROFILE_PHOTO_EVENT } from '@/features/chat/shellEvents'
import { forgetPushPromptDismissal } from '@/lib/push'

/* ------------------------------------------------------------------
   هویت — سند مادر بند ۶
   ------------------------------------------------------------------
   · یک حساب، یک Session، بدون Login جداگانه برای ماژول‌ها (AC-01)
   · Session روی Cookie امن است؛ هیچ Token در حافظه مرورگر ذخیره
     نمی‌شود (بند ۶.۱ و شکاف SG-04)
   · ورود با ایمیل یا موبایل + رمز (پاسخ ۵ بند ۲۸)
   ------------------------------------------------------------------ */

export interface LoginPayload {
  /** ایمیل یا شماره موبایل */
  identifier: string
  password: string
  remember?: boolean
}

export type LoginResult =
  | { twoFactorRequired: false }
  | {
      twoFactorRequired: true
      challengeId: string
      method: string
      digits: number
      period: number
    }

interface TwoFactorPayload {
  challengeId: string
  code: string
}

interface LoginResponseData {
  two_factor_required?: boolean
  challenge_id?: string
  method?: string
  challenge_meta?: {
    method?: string
    digits?: number
    period?: number
  }
}

interface AuthContextValue {
  user: AuthUser | null
  /** true تا زمانی که وضعیت اولیه Session مشخص نشده */
  initializing: boolean
  login: (payload: LoginPayload) => Promise<LoginResult>
  confirmTwoFactor: (payload: TwoFactorPayload) => Promise<void>
  logout: () => Promise<void>
  refresh: () => Promise<void>
  can: (permission: PermissionKey) => boolean
  canAny: (permissions: PermissionKey[]) => boolean
  canAll: (permissions: PermissionKey[]) => boolean
  hasScope: (scope: DataScope) => boolean
  /** آیا کاربر نقشِ «مدیر سیستم» دارد؟ فقط برای تصمیم‌های نمایشی. */
  isSuperAdmin: boolean
  /**
   * آیا این ماژولِ محدودشده برای کاربر باز است؟
   *
   * ⚠️ جایگزینِ `!module.superAdminOnly || isSuperAdmin` است. آن شرط در
   * چهار جا تکرار می‌شد و با افزودنِ استثنای نقشِ «هلپ‌دسک» باید در هر
   * چهار جا هم‌زمان عوض می‌شد — که یعنی دیر یا زود یکی عقب می‌ماند.
   */
  canSeeModule: (module: { superAdminOnly?: boolean; alsoVisibleToRoles?: string[] } | undefined) => boolean
  hasModuleAccess: (moduleKey: string, declaredKeys?: string[]) => boolean
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth باید داخل <AuthProvider> استفاده شود')
  return context
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [initializing, setInitializing] = useState(true)

  const loadCurrentUser = useCallback(async () => {
    const response = await api.get<{ data: AuthUser }>('/auth/me')
    setUser(response.data.data)
  }, [])

  /* وضعیت اولیه — آیا Session معتبری وجود دارد؟ */
  useEffect(() => {
    let cancelled = false

    const bootstrap = async () => {
      try {
        await loadCurrentUser()
      } catch (error) {
        // ۴۰۱ در بارگذاری اولیه طبیعی است: کاربر هنوز وارد نشده.
        // هر خطای دیگری هم نباید مانع رندر صفحه ورود شود.
        if (!cancelled && !(error instanceof ApiError && error.isUnauthenticated)) {
          console.warn('[auth] بارگذاری وضعیت اولیه ناموفق بود', error)
        }
        if (!cancelled) setUser(null)
      } finally {
        if (!cancelled) setInitializing(false)
      }
    }

    void bootstrap()
    return () => {
      cancelled = true
    }
  }, [loadCurrentUser])

  /*
    عکسِ نمایه در گفتگو عوض شد (`me.photoChanged`) — «عکسِ چت همه‌جا»: عکسِ
    نوارِ بالا از پروفایلِ تازه، و دفترچه و چارت با نشانیِ تازه. نشانیِ قبلی
    دیگر چیزی نمی‌دهد (کپیِ قبلی حذف شده)؛ ماندنش یعنی حروفِ اول تا بارگذاریِ
    دوباره.
  */
  const queryClient = useQueryClient()
  useEffect(() => {
    const onPhoto = () => {
      void loadCurrentUser().catch(() => {
        /* پروفایل بارِ بعد تازه می‌شود */
      })
      void queryClient.invalidateQueries({ queryKey: ['people'] })
    }
    window.addEventListener(PROFILE_PHOTO_EVENT, onPhoto)
    return () => window.removeEventListener(PROFILE_PHOTO_EVENT, onPhoto)
  }, [loadCurrentUser, queryClient])

  /* ابطال Session از سمت سرور (AC-03: خروج کارمند) */
  useEffect(() => {
    let ending = false
    setUnauthenticatedHandler(() => {
      /*
        ⚠️ اول خروج از گفتگو، مثلِ `logout`: قاب با `setUser(null)` برداشته
        می‌شود و نشستِ Rocket.Chat در مرورگر می‌ماند — کسی که بعداً گفتگو را
        مستقیم باز کند، گفتگوی همین کاربر را می‌دید. حداکثر چند ثانیه (`chatSession`).
      */
      if (ending) return
      ending = true
      void signOutOfChat().finally(() => {
        ending = false
        setUser(null)
      })
    })
  }, [])

  /* ------------------------------------------------------------------
     هویت روی گزارش خطا — فقط شناسه، نه نام و ایمیل (بند ۱۶).

     ⚠️ عمداً یک Effect روی خودِ `user` است و نه فراخوانی در login/logout:
     `setUser` از چند مسیر صدا زده می‌شود (ورود، تایید دو‌عاملی، خروج،
     ابطال Session از سمت سرور). هر مسیری که فراموش شود یعنی شناسه کاربرِ
     قبلی روی خطاهای بعدی می‌ماند.
     ------------------------------------------------------------------ */
  useEffect(() => {
    setObservabilityUser(user?.id ?? null)
  }, [user?.id])

  const login = useCallback(
    async (payload: LoginPayload): Promise<LoginResult> => {
      // Sanctum پیش از نخستین درخواست حالت‌دار، کوکی CSRF می‌خواهد
      await ensureCsrfCookie()
      const response = await api.post<{ data: LoginResponseData }>('/auth/login', payload)
      const result = response.data.data

      if (result.two_factor_required) {
        if (!result.challenge_id) {
          throw new Error('پاسخ ورود دومرحله‌ای فاقد challenge_id است')
        }

        return {
          twoFactorRequired: true,
          challengeId: result.challenge_id,
          method: result.method ?? result.challenge_meta?.method ?? 'totp',
          digits: result.challenge_meta?.digits ?? 6,
          period: result.challenge_meta?.period ?? 30,
        }
      }

      await loadCurrentUser()
      /* دروازهٔ اعلان با هر ورود دوباره می‌پرسد — `forgetPushPromptDismissal` */
      forgetPushPromptDismissal()
      return { twoFactorRequired: false }
    },
    [loadCurrentUser],
  )

  const confirmTwoFactor = useCallback(
    async ({ challengeId, code }: TwoFactorPayload) => {
      await api.post('/auth/two-factor', {
        challenge_id: challengeId,
        code,
      })
      await loadCurrentUser()
      forgetPushPromptDismissal()
    },
    [loadCurrentUser],
  )

  const logout = useCallback(async () => {
    /*
      ⚠️ پیش از خروج از پنل، چون قابِ گفتگو با `setUser(null)` از صفحه
      برداشته می‌شود و دیگر فرمانی به آن نمی‌رسد. توضیح در `chatSession`.
    */
    await signOutOfChat()

    try {
      await api.post('/auth/logout')
    } finally {
      // حتی اگر درخواست خروج شکست بخورد، وضعیت محلی پاک می‌شود
      setUser(null)
    }
  }, [])

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      initializing,
      login,
      confirmTwoFactor,
      logout,
      refresh: loadCurrentUser,
      can: (permission) => can(user, permission),
      canAny: (permissions) => canAny(user, permissions),
      canAll: (permissions) => canAll(user, permissions),
      hasScope: (scope) => hasScope(user, scope),
      isSuperAdmin: isSuperAdmin(user),
      canSeeModule: (module) => canSeeRestrictedModule(user, module),
      /*
        ==================================================================
        🔴 وقتی فراخوان کلیدی ندهد، از رجیستری خوانده می‌شود — نه از
        پیشوندِ نام ماژول.

        ⚠️ نسخهٔ قبلی این پیش‌فرض را نداشت و هر فراخوانی که کلید
        نمی‌فرستاد بی‌صدا به حدسِ پیشوندی برمی‌گشت. سایدبار و کاشیِ
        داشبورد هر دو همین کار را می‌کردند، پس «سطح دسترسی» و «تقویم
        سازمانی» برای همه خاکستری و غیرقابل‌کلیک بودند — با نشانگرِ
        «ممنوع» و بدون هیچ توضیحی.

        🔴 پیش‌فرضِ درست اینجا یعنی هیچ فراخوانی دیگر نمی‌تواند
        فراموشش کند.
        ==================================================================
      */
      hasModuleAccess: (moduleKey, declaredKeys) =>
        hasModuleAccess(user, moduleKey, declaredKeys ?? moduleDeclaredPermissions(moduleKey)),
    }),
    [user, initializing, login, confirmTwoFactor, logout, loadCurrentUser],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
